// Handle de producto y pasillo de la tienda online: `{nombre-en-slug}-{12 hex del uuid}`,
// o solo `{12 hex}` cuando el nombre no deja slug.
//
// MÓDULO PURO: sin imports de servidor ni `server-only`. Lo importa
// `lib/site/consumerHost.ts` (isHandle), que corre dentro del proxy.
//
// El nombre del handle es cosmético (SEO y legibilidad): lo que identifica la fila son los
// 12 hex del uuid. Por eso ningún valor cacheado puede depender del `namePart` de la URL.

/** Tope del slug del nombre. */
const MAX_NAME_SLUG = 60

const HANDLE_RE = /^(?:[a-z0-9]+(?:-[a-z0-9]+)*-)?[0-9a-f]{12}$/

/** Primeros 12 hex del uuid sin guiones, en minúsculas. */
export function hex12Of(id: string): string {
  return id.replace(/-/g, "").slice(0, 12).toLowerCase()
}

/**
 * Única función que arma handles (y contra la que se comparan): sin nombre queda solo el hex.
 */
export function formatHandle(namePart: string, hex12: string): string {
  return namePart ? `${namePart}-${hex12}` : hex12
}

/**
 * Nombre → slug: NFD sin diacríticos, minúsculas, todo lo que no sea [a-z0-9] pasa a "-",
 * colapsado y sin guiones en los bordes. Tope de 60: corta en el último "-" antes del 60;
 * si no hay ninguno (una sola palabra larga), corte duro en 60.
 */
export function slugifyName(name: string): string {
  const slug = name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  if (slug.length <= MAX_NAME_SLUG) return slug

  const head = slug.slice(0, MAX_NAME_SLUG)
  // El 61.º carácter es "-": la palabra de la frontera quedó completa dentro del tope.
  if (slug.charAt(MAX_NAME_SLUG) === "-") return head
  const lastDash = head.lastIndexOf("-")
  return lastDash > 0 ? head.slice(0, lastDash) : head
}

/** Handle canónico de una fila (producto o pasillo). */
export function entityHandle(name: string, id: string): string {
  return formatHandle(slugifyName(name), hex12Of(id))
}

/**
 * Lee el segmento de la URL. `decodeURIComponent` protegido (un `%` suelto no tumba la
 * página, da null), pasa a minúsculas y valida la forma. No consulta nada.
 */
export function parseEntityHandle(raw: string): { namePart: string; hex12: string } | null {
  let decoded: string
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    return null
  }
  const value = decoded.toLowerCase()
  if (!HANDLE_RE.test(value)) return null
  const hex12 = value.slice(-12)
  const namePart = value.length > 12 ? value.slice(0, -13) : ""
  return { namePart, hex12 }
}

/** Lo usan los hosts para distinguir un handle de otro segmento de la ruta. */
export function isHandle(raw: string): boolean {
  return parseEntityHandle(raw) !== null
}

/**
 * Rango de uuid que cubre todos los ids que empiezan con esos 12 hex. Se consulta con
 * `id >= lo` y `id <= hi` (índice de la PK); nunca con LIKE.
 */
export function uuidRangeForHex12(hex12: string): { lo: string; hi: string } {
  const head = `${hex12.slice(0, 8)}-${hex12.slice(8, 12)}`
  return {
    lo: `${head}-0000-0000-000000000000`,
    hi: `${head}-ffff-ffff-ffffffffffff`,
  }
}

/**
 * Elige la fila entre las que caen en el rango de uuid.
 * - 0 filas: none.
 * - 1 fila: esa; el nombre no decide (el ítem pudo haberse renombrado → el llamador
 *   redirige al handle canónico).
 * - Varias: desempata por el slug del nombre. Un solo acierto gana; si no hay ninguno o
 *   hay más de uno, es ambiguo (el llamador responde 404 y registra el evento).
 */
export function pickByHandle<T extends { id: string; name: string }>(
  rows: T[],
  namePart: string,
):
  | { kind: "one"; row: T }
  | { kind: "none" }
  | { kind: "ambiguous"; ids: string[] } {
  if (rows.length === 0) return { kind: "none" }
  if (rows.length === 1) return { kind: "one", row: rows[0] }

  const matches = rows.filter((r) => slugifyName(r.name) === namePart)
  if (matches.length === 1) return { kind: "one", row: matches[0] }
  const pool = matches.length > 1 ? matches : rows
  return { kind: "ambiguous", ids: pool.map((r) => r.id) }
}
